import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";

const TIERS = [
  {
    name: "Launch",
    tagline: "Your web presence, always current",
    monthly: 490,
    yearly: 4900,
    features: [
      "Custom website — live in a day",
      "Hosting, maintenance & updates",
      "Automated daily SEO blog engine",
      "Lead capture & contact forms",
      "Email support",
    ],
    popular: false,
  },
  {
    name: "Scale",
    tagline: "AI apps & agents shipped for you",
    monthly: 1490,
    yearly: 14900,
    features: [
      "Everything in Launch",
      "1 AI app or agent build per quarter",
      "LLM integrations (GPT, Gemini, Claude)",
      "Analytics & admin dashboards",
      "Priority support, 48h turnaround",
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    tagline: "A dedicated AI build squad",
    monthly: 3900,
    yearly: 39000,
    features: [
      "Everything in Scale",
      "Dedicated build team",
      "Unlimited agent iterations",
      "Custom integrations & SLAs",
      "Weekly strategy calls",
    ],
    popular: false,
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);

  return (
    <section id="pricing" className="py-24 relative border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-neon-cyan mb-3">// pricing</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight max-w-2xl">
            Simple plans. <span className="text-neon-yellow">Serious output.</span>
          </h2>
          <p className="mt-3 text-white/55 max-w-xl">
            Productized AI development on subscription — or use the{" "}
            <a href="#estimate" className="text-neon-cyan hover:underline">estimator</a> for a one-off custom build.
          </p>
        </div>

        {/* Monthly / Yearly toggle */}
        <div className="flex items-center gap-4 mb-10">
          <span className={`font-mono text-sm ${!yearly ? "text-neon-cyan" : "text-white/40"}`}>Monthly</span>
          <button
            data-testid="pricing-toggle"
            role="switch"
            aria-checked={yearly}
            onClick={() => setYearly(!yearly)}
            className={`relative w-14 h-7 rounded-full border transition-colors duration-300 ${
              yearly ? "bg-neon-cyan/20 border-neon-cyan" : "bg-white/5 border-white/20"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-[22px] h-[22px] rounded-full bg-neon-cyan shadow-[0_0_12px_rgba(0,255,156,0.6)] transition-transform duration-300 ${
                yearly ? "translate-x-7" : ""
              }`}
            />
          </button>
          <span className={`font-mono text-sm ${yearly ? "text-neon-cyan" : "text-white/40"}`}>Yearly</span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-neon-yellow border border-neon-yellow/30 bg-neon-yellow/10 rounded-full px-2.5 py-1">
            2 months free
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              className={`relative rounded-2xl border backdrop-blur-2xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                tier.popular
                  ? "border-neon-cyan/50 bg-neon-cyan/[0.05] shadow-[0_0_50px_rgba(0,255,156,0.12)]"
                  : "border-white/12 bg-white/[0.04] hover:border-white/25"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-neon-cyan text-black font-mono text-[10px] font-bold uppercase tracking-wider px-3 py-1">
                  <Zap size={11} /> Most popular
                </div>
              )}
              <h3 className="font-heading text-xl font-black">{tier.name}</h3>
              <p className="text-sm text-white/50 mt-1">{tier.tagline}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-mono text-4xl font-bold text-neon-cyan" data-testid={`pricing-price-${tier.name.toLowerCase()}`}>
                  ${(yearly ? tier.yearly : tier.monthly).toLocaleString()}
                </span>
                <span className="font-mono text-sm text-white/40">/{yearly ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                    <Check size={15} className="text-neon-cyan mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                className={`mt-8 text-center font-semibold text-sm px-6 py-3 rounded-full transition-all ${
                  tier.popular
                    ? "neon-btn bg-neon-cyan text-black"
                    : "border border-white/20 hover:border-neon-cyan hover:text-neon-cyan"
                }`}
              >
                Start with {tier.name}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
